import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { IconTrash, IconPlus, IconEdit, IconDots } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';
import { CustomInput } from '../CustomInput';

interface Product {
  id: string;
  name?: string;
  quantity?: string | number;
  price?: string | number;
}

// Helper function to safely parse numbers
const safeParseNumber = (value: string | number | undefined | null): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  if (typeof value === 'string') {
    if (value.trim() === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Helper function to calculate total for a product
const calculateProductTotal = (
  quantity: string | number | undefined,
  price: string | number | undefined
): number => {
  return safeParseNumber(quantity) * safeParseNumber(price);
};

export const DEFAULT_PRODUCTS = [];

export const Products = ({
  onChange,
  value,
  name,
  label,
  isDisabled,
}: {
  onChange?: (values: Product[]) => void;
  value?: Product[];
  name?: string;
  label?: string;
  isDisabled?: boolean;
}) => {
  const intl = useIntl();
  const [newProduct, setNewProduct] = useState<Product | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);

  const [products, setProducts] = useState<Product[]>(value || []);

  const addProduct = (_newProduct: Product | null) => {
    if (_newProduct) {
      setProducts([
        ...products,
        {
          ..._newProduct,
          quantity: _newProduct.quantity || '1',
          price: _newProduct.price || '0',
        },
      ]);
      setNewProduct(null);
    }
  };

  const updateProduct = (product: Product) => {
    setProducts(
      products.map((p) =>
        p.id === product.id
          ? {
              ...product,
              quantity: product.quantity || '1',
              price: product.price || '0',
            }
          : p
      )
    );
    setEditProduct(null);
  };

  const deleteProduct = (id: string) => {
    setProducts(products.filter((p) => p.id !== id));
  };

  useEffect(() => {
    if (onChange) {
      onChange(products);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  return (
    <Stack gap="md">
      <Text size="sm" fw={500}>
        {label}
      </Text>

      {products.map((product, index) => (
        <Box key={product.id}>
          <Flex gap="md" w="100%" justify="space-between" align="center">
            <Group gap={0} align="center">
              <Text c="dimmed">#</Text>
              <Text size="md" fw={700}>
                {index + 1}
              </Text>
            </Group>
            <Box style={{ flex: 1 }}>
              <Text size="sm">
                {product.name || intl.formatMessage({ id: 'productName' })}
              </Text>
              <Flex gap={5} align="center" wrap="wrap">
                <Text size="sm" c="dimmed">
                  {product.quantity || '0'}
                </Text>
                <Text size="sm" c="dimmed">
                  ×
                </Text>
                <Text size="sm" c="dimmed">
                  {safeParseNumber(product.price)} RON
                </Text>
                {/* <Text size="sm" c="dimmed">=</Text> */}
              </Flex>
            </Box>
            <Group>
              <Text size="sm" fw={600}>
                {calculateProductTotal(product.quantity, product.price)} RON
              </Text>
            </Group>
            <Flex gap="xs" align="center">
              <Menu position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="light" disabled={isDisabled}>
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => setEditProduct(product)}
                    disabled={isDisabled}
                  >
                    {intl.formatMessage({ id: 'edit' })}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    onClick={() => deleteProduct(product.id)}
                    disabled={isDisabled}
                    color="red"
                  >
                    {intl.formatMessage({ id: 'delete' })}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>
          </Flex>
        </Box>
      ))}

      <Button
        variant="light"
        size="xs"
        aria-label={intl.formatMessage({ id: 'addProduct' })}
        onClick={() =>
          setNewProduct({ id: uuidv4(), name: '', quantity: '', price: '' })
        }
        disabled={isDisabled}
        leftSection={<IconPlus size={16} />}
      >
        {intl.formatMessage({ id: 'addProduct' })}
      </Button>

      {products.length > 0 && (
        <Flex w="100%" justify="space-between">
          <Text size="sm">{intl.formatMessage({ id: 'totalPayment' })}</Text>
          <Text size="sm" fw={700}>
            {products.reduce(
              (acc, product) =>
                acc + calculateProductTotal(product.quantity, product.price),
              0
            )}{' '}
            RON
          </Text>
        </Flex>
      )}

      <Modal
        opened={newProduct !== null || editProduct !== null}
        centered
        onClose={() => {
          setNewProduct(null);
          setEditProduct(null);
        }}
        title={
          newProduct
            ? intl.formatMessage({ id: 'addNewProduct' })
            : intl.formatMessage({ id: 'editProduct' })
        }
        zIndex={1000}
      >
        <Stack gap="md">
          <CustomInput
            label={intl.formatMessage({ id: 'name' })}
            name="name"
            type="text"
            value={
              newProduct ? newProduct.name : editProduct ? editProduct.name : ''
            }
            onChange={(value) =>
              newProduct
                ? setNewProduct({
                    ...newProduct,
                    name: value,
                  })
                : editProduct
                  ? setEditProduct({
                      ...editProduct,
                      name: value,
                    })
                  : null
            }
          />
          <CustomInput
            label={intl.formatMessage({ id: 'quantity' })}
            name="quantity"
            type="number"
            minValue={0}
            value={
              (newProduct
                ? newProduct.quantity
                : editProduct
                  ? editProduct.quantity
                  : ''
              )?.toString() || ''
            }
            onChange={(value) =>
              newProduct
                ? setNewProduct({
                    ...newProduct,
                    quantity: value,
                  })
                : editProduct
                  ? setEditProduct({
                      ...editProduct,
                      quantity: value,
                    })
                  : null
            }
          />
          <CustomInput
            label={intl.formatMessage({ id: 'price' })}
            name="price"
            type="number"
            minValue={0}
            value={
              (newProduct
                ? newProduct.price
                : editProduct
                  ? editProduct.price
                  : ''
              )?.toString() || ''
            }
            onChange={(value) =>
              newProduct
                ? setNewProduct({
                    ...newProduct,
                    price: value,
                  })
                : editProduct
                  ? setEditProduct({
                      ...editProduct,
                      price: value,
                    })
                  : null
            }
          />

          <Group justify="flex-end" mt="md">
            <Button
              aria-label={intl.formatMessage({ id: 'cancel' })}
              variant="default"
              onClick={() => {
                setNewProduct(null);
                setEditProduct(null);
              }}
            >
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              aria-label={intl.formatMessage({ id: 'add' })}
              onClick={() =>
                newProduct
                  ? addProduct(newProduct)
                  : editProduct
                    ? updateProduct(editProduct)
                    : null
              }
            >
              {newProduct && intl.formatMessage({ id: 'add' })}
              {editProduct && intl.formatMessage({ id: 'edit' })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
